import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/photographer-real.jpg";

export const photographers: Persona = {
  slug: "photographers-videographers",
  profession: { hu: "fotós", en: "photographer" },
  professionPlural: {
    hu: "fotósok és videósok",
    en: "photographers and videographers",
  },
  professionGenitive: { hu: "fotósként", en: "as a photographer" },
  heroH1: {
    hu: "Portfólió, foglalás, csomagok: egy chat üzenetből.",
    en: "Portfolio, bookings, packages: from one chat message.",
  },
  heroSub: {
    hu: "AI weboldal, online foglalás, csomag-értékesítés és galéria-átadás: egy platformon fotósoknak és kis stúdióknak.",
    en: "AI website, online booking, package sales and gallery delivery: one platform for photographers and small studios.",
  },
  heroImage,
  heroImageAlt: "Photographer running a portrait shoot in the studio",
  chatExamplePrompt: {
    hu: "Készíts egy csomagot esküvői fotózásra, 280 000 Ft",
    en: "Create a wedding photo package, 280,000 HUF",
  },
  chatExampleResponse: {
    hu: "Létrehoztam: 10 órás esküvői csomag, 600+ retusált kép, online galéria, 280 000 Ft. Élesítve a weboldalon.",
    en: "Created: 10-hour wedding package, 600+ retouched photos, online gallery, 280,000 HUF. Live on the site.",
  },
  accentColor: "indigo",

  defaultMode: "solo",
  teamSize: { min: 1, max: 5 },
  soloLabel: { hu: "Szóló fotós", en: "Solo photographer" },
  teamLabel: {
    hu: "Stúdió több fotóssal",
    en: "Studio with multiple photographers",
  },

  soloDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Az AI riportot küld: 2 új lead esküvői fotózásra. Mindkettőnek elküldte az ajánlatot a stílusodban.",
        en: "AI sends a report: 2 new wedding photo leads. Both got a quote in your tone.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "Family shoot a parkban. A kliens kartonján már látod a kapcsolatot és a csomag részleteit.",
        en: "Family shoot in the park. The client card shows contact info and package details.",
      },
    },
    {
      time: "14:00",
      what: {
        hu: "Új csomagot hozol létre chatben: 'Mini portré, 30 perc, 35 000 Ft'. 30 mp múlva élesítve.",
        en: "You create a new package in chat: 'Mini portrait, 30 min, 35,000 HUF'. Live in 30 seconds.",
      },
    },
    {
      time: "17:00",
      what: {
        hu: "Egy ügyfél fizet előleget. Számla generálva, naptárba beírva, előkészítő anyagok elküldve.",
        en: "A client pays a deposit. Invoice generated, calendar updated, prep materials sent.",
      },
    },
    {
      time: "20:00",
      what: {
        hu: "Galéria-átadás: 120 retusált kép online elérhető a vendég saját aloldalán.",
        en: "Gallery delivery: 120 retouched photos available on the client's private subpage.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Stúdió dashboard: 3 fotósod 5 sessiont csinál ma. Mindenki naptára átláthatóan.",
        en: "Studio dashboard: your 3 photographers run 5 sessions today. All calendars at a glance.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "Új kliens esküvőre érdeklődik: az AI a stílus alapján Lillát ajánlja (klasszikus dokumentarista).",
        en: "New client inquiring about a wedding: AI recommends Lilla based on style (classic documentary).",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Egy fotós beteg. Az AI áthelyezi 2 sessionjét másik kollégához, klienseket értesít, asszisztens megerősítve.",
        en: "A photographer is sick. AI moves their 2 sessions to a colleague, notifies clients, assistant confirmed.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Az AI mutatja: Bence portré-shoot konverziója 38%-kal magasabb. Megmutatod a csapatnak a működését.",
        en: "AI shows: Bence's portrait shoot conversion is 38% higher. You share his approach with the team.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Hétvégi mini-shoot akció telik. Az AI várólistára teszi a túljelentkezőket.",
        en: "Weekend mini-shoot promo fills up. AI puts overflow on the waitlist.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Bevétel fotósonként, jutalék-elszámolás, szerkesztő-asszisztens órabér kiszámítva.",
        en: "Revenue per photographer, commission ready, editor-assistant hourly pay calculated.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: { hu: "Minden egy fiókban", en: "Everything in one account" },
      body: {
        hu: "Naptár, kliens, csomag, galéria, számla: egyetlen rendszerben. Nem kell Calendly + Pixieset + külön CRM.",
        en: "Calendar, clients, packages, galleries, invoices: one system. No Calendly + Pixieset + separate CRM.",
      },
    },
    {
      icon: "Bot",
      title: {
        hu: "Lead-űrlap + portfólió-küldés",
        en: "Lead form + portfolio send",
      },
      body: {
        hu: "Új érdeklődő kitölti az online űrlapot, automata válasz-email megy ki a portfólióval, árakkal és foglalási linkkel a discovery hívásra.",
        en: "New leads fill out the online form, an auto-reply email goes out with portfolio, pricing and a discovery-call booking link.",
      },
    },
    {
      icon: "CreditCard",
      title: { hu: "Előleg foglaláskor", en: "Deposit at booking" },
      body: {
        hu: "Automatikus előleg-levonás foglaláskor, fennmaradó összeg helyszínen vagy átutalással: automata számlával.",
        en: "Auto-deducted deposit at booking, remainder on-site or by transfer: auto-invoiced.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan fotós, egy ár",
        en: "Unlimited photographers, one price",
      },
      body: {
        hu: "1 vagy 5 fő: ugyanaz a havidíj. Mindenki saját naptárral, kliens-körrel, portfólióval.",
        en: "1 or 5: same monthly fee. Each with their own calendar, clients, portfolio.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Jogosultság-kezelés", en: "Role-based access" },
      body: {
        hu: "Tulaj, asszisztens, fotós: mindenki csak azt látja, amihez joga van.",
        en: "Owner, assistant, photographer: everyone sees only what they're allowed to.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja a lead-eket", en: "AI routes leads" },
      body: {
        hu: "Új kliens: az AI stílus (esküvő, portré, food) alapján a megfelelő fotóshoz osztja.",
        en: "New client: AI assigns by style (wedding, portrait, food) to the right photographer.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Jutalék és asszisztens-óra",
        en: "Commission and assistant hours",
      },
      body: {
        hu: "Ki mennyit dolgozott, mennyi retusban segített: exportálható elszámolás.",
        en: "Who shot how much, who helped with retouching: exportable settlement.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Camera",
      title: { hu: "Portfólió-oldal", en: "Portfolio page" },
      body: {
        hu: "AI által szerkesztett portfólió, kategóriák szerint, mobilra optimalizált. Új projektet chatben raksz fel.",
        en: "AI-curated portfolio by category, mobile-optimized. Add new projects via chat.",
      },
    },
    {
      icon: "Calendar",
      title: { hu: "Csomag-értékesítés", en: "Package sales" },
      body: {
        hu: "Esküvő, családi, üzleti: minden csomag egy oldalon, online fizetéssel és előleggel.",
        en: "Wedding, family, business: every package on one page with online payment and deposit.",
      },
    },
    {
      icon: "CreditCard",
      title: { hu: "Előleg foglaláskor", en: "Deposit at booking" },
      body: {
        hu: "Automatikus előleg-levonás foglaláskor, fennmaradó összeg helyszínen vagy átutalással, automata számlával.",
        en: "Auto-deducted deposit at booking, remainder on-site or by transfer, auto-invoiced.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "Marketing AI", en: "Marketing AI" },
      body: {
        hu: "Instagram poszt minden új projektből, follow-up email kampány, ajánlói rendszer.",
        en: "Instagram post from every new project, follow-up email campaigns, referral system.",
      },
    },
  ],

  objections: [
    {
      question: {
        hu: "A portfólióm a lényeg: egy generikus weboldal nem lesz elég profi",
        en: "My portfolio is the point: a generic site won't look pro",
      },
      answer: {
        hu: "A Bespire portfólió-oldal teljesen testreszabható: saját aldomain, saját színek, projektenkénti egyedi layout. Az AI a te stílusodban szerkeszt, nem sablon-szerű.",
        en: "Bespire's portfolio page is fully customizable: own subdomain, own colors, custom layout per project. AI edits in your style, not template-like.",
      },
    },
    {
      question: {
        hu: "Csapatban dolgozunk: hogy kezeli a stúdiót?",
        en: "We work as a team: how does it handle the studio?",
      },
      answer: {
        hu: "Minden fotósnak saját portfólió-aldomainje, kliens-listája, statisztikája van. Te tulajdonosként mindent látsz. Korlátlan fotóst vehetsz fel, NEM fizetsz többet érte (1 vagy 5 fő: ugyanaz a 10 000 Ft + ÁFA / hó).",
        en: "Each photographer has their own portfolio subdomain, clients and stats. As owner you see everything. Add unlimited photographers, you do NOT pay more (1 or 5, same 10,000 HUF + VAT / month).",
      },
    },
    {
      question: {
        hu: "Hogy adom át a kész képeket?",
        en: "How do I deliver finished photos?",
      },
      answer: {
        hu: "Saját online galéria minden vendégnek: jelszavas védelem, letöltés, megrendelés print/album-ra. A vendég saját aloldalon látja a képeit, nem kell WeTransfer link küldözgetés.",
        en: "Private online gallery for every client: password-protected, downloads, print/album orders. The client sees their photos on a subpage: no more WeTransfer links.",
      },
    },
    {
      question: {
        hu: "Az ügyfeleim 50%-os előleget fizetnek készpénzben: kell az online fizetés?",
        en: "My clients pay 50% deposit in cash: do I need online payment?",
      },
      answer: {
        hu: "Nem kell. A foglalás történhet előleg nélkül is, vagy átutalási megerősítéssel. Az online kártyás fizetés OPCIÓ, nem kötelező. Te döntöd el csomagonként.",
        en: "No. Bookings can happen without deposit or via bank-transfer confirmation. Online card payment is OPTIONAL, not required. You decide per package.",
      },
    },
  ],

  monetization: [
    {
      icon: "Heart",
      title: { hu: "Esküvő és nagy események", en: "Weddings and big events" },
      examples: [
        { hu: "10 órás esküvői csomag", en: "10-hour wedding package" },
        { hu: "Eljegyzés-fotózás", en: "Engagement shoot" },
        { hu: "Polgári + lakodalom", en: "Civil + reception" },
      ],
      priceHint: {
        hu: "Példa: 4 esküvő × 320 000 Ft = 1 280 000 Ft / hó",
        en: "Example: 4 weddings × 320,000 HUF = 1,280,000 HUF / month",
      },
    },
    {
      icon: "Smile",
      title: { hu: "Portré és családi", en: "Portrait and family" },
      examples: [
        { hu: "Family shoot a parkban", en: "Family shoot in the park" },
        { hu: "Newborn", en: "Newborn" },
        { hu: "Senior portré", en: "Senior portrait" },
      ],
      priceHint: {
        hu: "Példa: 18 shoot × 65 000 Ft = 1 170 000 Ft / hó",
        en: "Example: 18 shoots × 65,000 HUF = 1,170,000 HUF / month",
      },
    },
    {
      icon: "Briefcase",
      title: { hu: "Üzleti és termék", en: "Business and product" },
      examples: [
        { hu: "Cég-portré csomag", en: "Corporate portrait package" },
        { hu: "Termékfotózás", en: "Product photography" },
        { hu: "Event coverage", en: "Event coverage" },
      ],
      priceHint: {
        hu: "Példa: 6 üzleti × 145 000 Ft = 870 000 Ft / hó",
        en: "Example: 6 business × 145,000 HUF = 870,000 HUF / month",
      },
    },
    {
      icon: "Video",
      title: { hu: "Videó és kreatív", en: "Video and creative" },
      examples: [
        { hu: "Reels-csomag (5 db)", en: "Reels bundle (5 pcs)" },
        { hu: "Esküvői highlight videó", en: "Wedding highlight video" },
        { hu: "Termék-bemutató videó", en: "Product showcase video" },
      ],
      priceHint: {
        hu: "Példa: 5 videó × 85 000 Ft = 425 000 Ft / hó",
        en: "Example: 5 videos × 85,000 HUF = 425,000 HUF / month",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Tagság és kiterjesztések", en: "Membership and add-ons" },
      examples: [
        { hu: "Éves család-fotó tagság", en: "Annual family photo membership" },
        { hu: "Print-album upsell", en: "Print-album upsell" },
        {
          hu: "Galéria-tárhely meghosszabbítás",
          en: "Gallery storage extension",
        },
      ],
      priceHint: {
        hu: "Stúdió-szint: 3 fotós × 15 tagsági kliens × 18 000 Ft = 810 000 Ft / hó potenciál",
        en: "Studio level: 3 photographers × 15 members × 18,000 HUF = 810,000 HUF / month potential",
      },
    },
  ],

  results: [
    {
      value: "+62%",
      label: { hu: "esküvői csomag-eladás", en: "wedding package sales" },
    },
    {
      value: "-75%",
      label: { hu: "email-egyeztetés", en: "email back-and-forth" },
    },
    { value: "4.9★", label: { hu: "vendég-NPS", en: "client NPS" } },
    {
      value: "24 óra",
      label: { hu: "galéria-átadás", en: "gallery delivery" },
    },
  ],
  testimonial: {
    quote: {
      hu: "A vendégek a saját galériájukat látják jelszóval, megrendelhetnek printet és albumot. Nincs több WeTransfer link: és +30% extra bevétel a print-ekből.",
      en: "Clients see their own password-protected gallery and can order prints and albums. No more WeTransfer links: and +30% extra revenue from prints.",
    },
    name: "Molnár Dániel",
    role: { hu: "Esküvői fotós", en: "Wedding photographer" },
    city: { hu: "Budapest, XIII. kerület", en: "Budapest, District XIII" },
    avatarInitials: "MD",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a csomagjaid",
        en: "Tell us your packages in chat",
      },
      body: {
        hu: "Esküvő, portré, családi, eljegyzés: az AI strukturálja és árazza.",
        en: "Wedding, portrait, family, engagement: AI structures and prices them.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti a portfólió-oldalt",
        en: "AI builds your portfolio site",
      },
      body: {
        hu: "Csomagok, foglalás, előleg, online galéria-funkció.",
        en: "Packages, booking, deposit, online gallery.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Importáljuk a régi munkákat",
        en: "We import your past work",
      },
      body: {
        hu: "Galériák, vendégek, korábbi szerződések.",
        en: "Galleries, clients, past contracts.",
      },
    },
    {
      step: 4,
      title: {
        hu: "Élő, vendégek foglalnak online",
        en: "Live, clients book online",
      },
      body: {
        hu: "Előleg, szerződés, automata galéria-átadás.",
        en: "Deposit, contract, automated gallery delivery.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Stripe",
    "SimplePay",
    "Apple Pay",
    "Google Pay",
    "Meta",
    "WhatsApp Business",
    "Pixieset",
    "Dropbox",
  ],
  breadcrumbLabel: {
    hu: "Fotósok és videósok",
    en: "Photographers & videographers",
  },
  similarPersonas: [
    "consultants-business-coaches",
    "beauticians-beauty-salons",
    "hairdressers-barbers",
  ],
};
